import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, MouseEventHandler, useState } from 'react'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import ProfileRegistry, { IProfile } from '../../../stores/registry/ProfileRegistry'
import { ArpMediaType } from '../../../types/common'
import Button from '../../core/Button/Button'
import { TwitterLogo } from '../../core/icons'
import { useStatus } from '../../core/Utils'
import withTooltip from '../../core/withTooltip'
import { Spinner, useAsyncEffect, useCollaborators } from '../../platform/explore/common/utils'

const ButtonWithTooltip = withTooltip(Button)

type TweetButtonProps = {
  mediaType: ArpMediaType
  index: number
} & ComponentProps<typeof ButtonWithTooltip>

export const ARPEGGI_TWITTER_NICKNAME = 'arpeggi_labs'
export const TWEET_BASE_URL = 'https://twitter.com/intent/tweet?text='

const LINK_SIZE = 25
const TWEET_MAX_SIZE = 280
const buildAndFitToLimit = (baseText: string, featured: string[], link: string) => {
  const sortedByHandle = featured.sort((a, b) => a.charCodeAt(0) - b.charCodeAt(0))
  if (baseText.length + LINK_SIZE + 1 > TWEET_MAX_SIZE)
    throw new Error('[tweet-builder] tweet base text size is already too big.')
  let finalText = `${baseText} featuring content from ${sortedByHandle.join(', ')}\n`
  let size = sortedByHandle.length
  while (finalText.length + 1 + LINK_SIZE > TWEET_MAX_SIZE) {
    if (size === 0) break
    size--
    finalText = `${baseText} featuring content from ${sortedByHandle.slice(0, size).join(', ')}\n`
  }
  return `${finalText}${link}`
}

const getHandleOrDisplayName = ({ twitter_handle, display_name }: IProfile) => {
  if (!display_name) throw new Error('display_name is undefined or null')
  return twitter_handle ? `@${twitter_handle}` : display_name
}

const buildTweetText = async (type: 'song' | 'stem' | 'sample', index: number, eths?: string[]) => {
  const media = await ArpRegistry.getByIdAsync(index)
  const eth = media.user.walletAddress
  const artistNickname = media.user.displayName

  const mediaURL = `${window.location.origin}/${type}?token=${index}`
  const name = type === 'stem' ? 'this loop' : media.metadata.title
  const text = `Check out ${name} by ${artistNickname} on @${ARPEGGI_TWITTER_NICKNAME}`

  if (type === 'sample' || eths.length < 2) return `${text} ${mediaURL}`

  const collabs = await ProfileRegistry.getByIndexBulk(eths.filter((e) => e !== eth))
  const nicknames = collabs.map((p) => (p.twitter_handle ? `@${p.twitter_handle}` : p.display_name))
  return buildAndFitToLimit(text, nicknames, mediaURL)
}

const tweet = (tweetText: string) => {
  window.open(encodeURI(TWEET_BASE_URL + tweetText), '_blank')
}

export const useTweetArpMedia = (index: number, mediaType: ArpMediaType) => {
  const { uniqueCollabEthAddresses, status } = useCollaborators(mediaType, index.toString())
  const [tweetStatus, setTweetStatus] = useStatus()
  const [text, setText] = useState('')

  useAsyncEffect(async () => {
    if (status !== 'ready') return
    const tweetText = await buildTweetText(mediaType, index, uniqueCollabEthAddresses)
    setText(tweetText)
    setTweetStatus('ready')
  }, [mediaType, index, status])

  const handler = () => tweet(text)
  return { status: tweetStatus, tweet: handler }
}

const TweetButton: FC<TweetButtonProps> = ({ mediaType, index, onClick, ...props }) => {
  const { status, tweet } = useTweetArpMedia(index, mediaType)

  const handleTweetButton: MouseEventHandler<HTMLButtonElement> = (evt) => {
    tweet()
    onClick && onClick(evt)
  }
  return (
    <ButtonWithTooltip
      type={'outlined'}
      bigSquare
      rounded
      disabled={status === 'loading'}
      tooltipText={'Share via Twitter'}
      onClick={handleTweetButton}
      {...props}
    >
      {status === 'ready' && <TwitterLogo />}
      {status === 'loading' && <Spinner />}
    </ButtonWithTooltip>
  )
}

export default observer(TweetButton)
